import React from "react";

import * as dialogs from "@client/features/dialogs";

import * as utils from "@client/utils";

import {ManagerTable} from "@client/components/ManagerTable";
import {ContentModal} from "@client/components/Modal";
import {processResponse} from "@client/components/Workout";
import Dialogs from "@client/containers/Dialogs";
import {ManagerLayout} from "@client/site/ManagerLayout";
import * as forms from "@client/site/forms";
import {useAppDispatch} from "@client/store";
import {reverse, server} from "@reactivated";

export const Template = (props: server.manager.templates.ProgramList) => {
    const [programs, setPrograms] = React.useState(props.programs);
    const [isCreating, setIsCreating] = React.useState(false);
    const form = forms.useAutoPform(server.journal.workouts.WorkoutGroupForm);
    const dispatch = useAppDispatch();

    const columns = [
        {name: "Name", accessor: "name"},
        {name: "Last updated", accessor: "updated"},
        {name: "Clients", accessor: "clients"},
        {name: "Actions", accessor: "actions", type: "actions" as const},
    ];
    const values = programs.map((program) => ({
        name: program.name,
        updated: `${utils.formatDistanceToNow(program.updated_at)} ago`,
        clients: program.instances.filter((u) => u.is_active).length,
        link: reverse("program_detail", {program_id: program.id}),
        actions: (
            <div className="flex gap-2">
                <button
                    type="button"
                    onClick={async () => {
                        const existingNames = programs.map((p) => p.name ?? "");
                        const name = utils.generateCopyLabel(
                            program.name ?? "",
                            existingNames,
                        );
                        const result = await processResponse(
                            server.journal.workouts.copy_program({
                                source_program: program.uuid,
                                name,
                            }),
                        );

                        if (result.type != "success") {
                            throw new Error("Cannot happen");
                        }

                        setPrograms([
                            {exercise_type_maxes_required: [], ...result.data},
                            ...programs,
                        ]);
                    }}
                    className="
                        truncate rounded-sm bg-white px-2 py-1 text-xs font-semibold
                        text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset
                        hover:bg-gray-50
                        disabled:border-slate-200 disabled:bg-slate-50
                        disabled:text-slate-500 disabled:shadow-none
                    "
                >
                    Copy
                </button>
                <button
                    type="button"
                    onClick={async () => {
                        const confirmed = await dispatch(
                            dialogs.confirm({
                                message: "Are you sure?",
                                body: "This program will be deleted forever.",
                            }),
                        );

                        if (confirmed) {
                            await server.journal.workouts.delete_program({
                                source_program: program.uuid,
                            });

                            window.location.reload();
                        }
                    }}
                    className="
                        truncate rounded-sm bg-white px-2 py-1 text-xs font-semibold
                        text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset
                        hover:bg-gray-50
                        disabled:border-slate-200 disabled:bg-slate-50
                        disabled:text-slate-500 disabled:shadow-none
                    "
                >
                    Delete
                </button>
            </div>
        ),
    }));

    const createProgram = async () => {
        const result = await processResponse(
            server.journal.workouts.create_program({
                uuid: crypto.randomUUID(),
                creation: {
                    type: "CUSTOM",
                    mode: "TEMPLATE",
                    data: null,
                },
                template: {
                    name: form.values.name,
                    description: "",
                    visibility: "PRIVATE",
                },
                program_users: null,
                exercise_maxes: [],
                workout_groups: [
                    {
                        uuid: crypto.randomUUID(),
                        name: "Week 1",
                        description: "",
                        workouts: [
                            {
                                uuid: crypto.randomUUID(),
                                name: "Workout 1",
                                instructions: "",
                                maxes: "EXISTING_MAXES",
                                mode: "NORMAL",
                                questions: [],
                                sections: [],
                            },
                        ],
                    },
                ],
            }),
        );

        if (result.type == "success") {
            window.location.reload();
        }
    };
    const createSampleProgram = async () => {
        const createSet = (
            weight_percentage: string,
        ): server.journal.workouts.ExerciseSet.output => ({
            uuid: crypto.randomUUID(),
            type: "WORKING",
            instructions: "",
            weight_type: "PERCENTAGE",
            weight_percentage: weight_percentage,
            distance_type: null,
            duration_type: null,
            speed_type: null,
            duration: null,
            distance: null,
            speed: null,
            incline: null,
            resistance: null,
            reps: 5,
            rpe: null,
            weight: null,
            status: "INCOMPLETE",
            attributes: [],
            progression: null,
        });
        const createExercises = (
            weight_percentage: string,
        ): Array<server.journal.workouts.WorkoutSection.input> => [
            {
                uuid: crypto.randomUUID(),
                type: "BASIC",
                name: null,
                instructions: "",
                duration: null,
                rounds: null,
                copied_from: null,
                groups: [
                    {
                        uuid: crypto.randomUUID(),
                        exercises: [
                            {
                                uuid: crypto.randomUUID(),
                                mode: "NORMAL",
                                structure: "STRAIGHT",
                                type: {
                                    uuid: "b39f8b5e-ae3a-4888-b8b1-ba35583e4a74",
                                },
                                instructions: "",
                                progression: null,
                                video_uid: null,
                                rest_duration: null,
                                sets: [
                                    createSet(weight_percentage),
                                    createSet(weight_percentage),
                                    createSet(weight_percentage),
                                    createSet(weight_percentage),
                                    createSet(weight_percentage),
                                ],
                            },
                        ],
                    },
                    {
                        uuid: crypto.randomUUID(),
                        exercises: [
                            {
                                uuid: crypto.randomUUID(),
                                mode: "NORMAL",
                                structure: "STRAIGHT",
                                type: {
                                    uuid: "12fe4e86-47bf-4548-b7e5-5344079ea4eb",
                                },
                                instructions: "",
                                progression: null,
                                video_uid: null,
                                rest_duration: null,
                                sets: [
                                    createSet(weight_percentage),
                                    createSet(weight_percentage),
                                    createSet(weight_percentage),
                                    createSet(weight_percentage),
                                    createSet(weight_percentage),
                                ],
                            },
                        ],
                    },
                ],
            },
        ];

        const result = await server.journal.workouts.create_program({
            uuid: crypto.randomUUID(),
            creation: {
                type: "CUSTOM",
                mode: "TEMPLATE",
                data: null,
            },
            program_users: null,
            exercise_maxes: [],
            workout_groups: [
                {
                    uuid: crypto.randomUUID(),
                    name: "Week 1",
                    description: "",
                    workouts: [
                        {
                            uuid: crypto.randomUUID(),
                            name: "Monday",
                            instructions: "",
                            maxes: "EXISTING_MAXES",
                            mode: "NORMAL",
                            sections: createExercises("70"),
                            questions: [],
                        },
                        {
                            uuid: crypto.randomUUID(),
                            name: "Wednesday",
                            instructions: "",
                            maxes: "EXISTING_MAXES",
                            mode: "NORMAL",
                            sections: createExercises("75"),
                            questions: [],
                        },
                        {
                            uuid: crypto.randomUUID(),
                            name: "Friday",
                            instructions: "",
                            maxes: "EXISTING_MAXES",
                            mode: "NORMAL",
                            sections: createExercises("80"),
                            questions: [],
                        },
                    ],
                },
                {
                    uuid: crypto.randomUUID(),
                    name: "Week 2",
                    description: "",
                    workouts: [
                        {
                            uuid: crypto.randomUUID(),
                            name: "Monday",
                            instructions: "",
                            maxes: "EXISTING_MAXES",
                            mode: "NORMAL",
                            sections: createExercises("75"),
                            questions: [],
                        },
                        {
                            uuid: crypto.randomUUID(),
                            name: "Wednesday",
                            instructions: "",
                            maxes: "EXISTING_MAXES",
                            mode: "NORMAL",
                            sections: createExercises("80"),
                            questions: [],
                        },
                        {
                            uuid: crypto.randomUUID(),
                            name: "Friday",
                            instructions: "",
                            maxes: "EXISTING_MAXES",
                            mode: "NORMAL",
                            sections: createExercises("85"),
                            questions: [],
                        },
                    ],
                },
                {
                    uuid: crypto.randomUUID(),
                    name: "Week 3",
                    description: "",
                    workouts: [
                        {
                            uuid: crypto.randomUUID(),
                            name: "Monday",
                            instructions: "",
                            maxes: "EXISTING_MAXES",
                            mode: "NORMAL",
                            sections: createExercises("80"),
                            questions: [],
                        },
                        {
                            uuid: crypto.randomUUID(),
                            name: "Wednesday",
                            instructions: "",
                            maxes: "EXISTING_MAXES",
                            mode: "NORMAL",
                            sections: createExercises("85"),
                            questions: [],
                        },
                        {
                            uuid: crypto.randomUUID(),
                            name: "Friday",
                            instructions: "",
                            maxes: "EXISTING_MAXES",
                            mode: "NORMAL",
                            sections: createExercises("90"),
                            questions: [],
                        },
                    ],
                },
            ],
            template: {
                name: "Strength & Conditioning",
                description: `This program focuses on building base levels of strength across the board.

It runs for 3 weeks with 3 workouts each week.

You’ll focus on a main compound lift every day.

After each main lift, you’ll perform accessory movements that complement that main lifts.`,
                visibility: "PRIVATE",
            },
        });

        if (result.type == "success") {
            window.location.reload();
        }
    };

    return (
        <ManagerLayout title="Programs" currentClientManagerId={null}>
            <Dialogs />
            <ContentModal isOpen={isCreating} setOpen={setIsCreating}>
                <form
                    autoComplete="off"
                    noValidate
                    onSubmit={(event) => {
                        event.preventDefault();
                        void createProgram();
                    }}
                    className="flex flex-col gap-4 p-6"
                >
                    <h2 className="text-xl whitespace-nowrap">Create program</h2>

                    <forms.PField field={form.fields.name} />

                    <button
                        type="submit"
                        disabled={form.values.name == ""}
                        className="
                            rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold
                            text-white shadow-xs
                            hover:bg-indigo-500
                            focus-visible:outline-2 focus-visible:outline-offset-2
                            focus-visible:outline-indigo-600 focus-visible:outline-solid
                            disabled:border-slate-200 disabled:bg-slate-50
                            disabled:text-slate-500 disabled:shadow-none
                        "
                    >
                        Create
                    </button>
                </form>
            </ContentModal>

            <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                    <h1 className="flex-1 text-2xl/6 text-gray-900">Programs</h1>
                    <button
                        type="button"
                        onClick={() => createSampleProgram()}
                        className="
                            rounded-md bg-indigo-600 px-2 py-1 text-xs font-semibold
                            text-white shadow-xs
                            hover:bg-indigo-500
                            focus-visible:outline-2 focus-visible:outline-offset-2
                            focus-visible:outline-indigo-600 focus-visible:outline-solid
                            disabled:border-slate-200 disabled:bg-slate-50
                            disabled:text-slate-500 disabled:shadow-none
                        "
                    >
                        Sample program
                    </button>
                    <button
                        type="button"
                        onClick={() => setIsCreating(true)}
                        className="
                            rounded-md bg-indigo-600 px-2 py-1 text-xs font-semibold
                            text-white shadow-xs
                            hover:bg-indigo-500
                            focus-visible:outline-2 focus-visible:outline-offset-2
                            focus-visible:outline-indigo-600 focus-visible:outline-solid
                            disabled:border-slate-200 disabled:bg-slate-50
                            disabled:text-slate-500 disabled:shadow-none
                        "
                    >
                        New program
                    </button>
                </div>
                <ManagerTable columns={columns} values={values} />
            </div>
        </ManagerLayout>
    );
};
